import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Check, ChevronRight } from "lucide-react";

export function ScheduleSection({ testid, Icon, title, subtitle, accentBg, accentIcon, countBadge, rows, emptyText, showOverdueDays, rowClick, defaultOpen = false, hideProject = false }) {
  // Collapsed by default — user opens each section on demand.
  const [collapsed, setCollapsed] = useState(!defaultOpen);
  return (
    <div className={`rounded-md3-lg border ${accentBg}`} data-testid={testid}>
      <button
        type="button"
        onClick={() => setCollapsed(c => !c)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-black/[.02] transition-colors"
        data-testid={`${testid}-toggle`}
      >
        <div className="flex items-center gap-3">
          <div className={`${accentIcon} p-2 rounded-md3-md`}>
            <Icon className="h-4 w-4" />
          </div>
          <div className="text-left">
            <div className="text-md3-title-medium font-semibold text-md3-on-surface flex items-center gap-2">
              {title}
              <span className={`${countBadge} text-xs font-semibold rounded-full px-2 py-0.5`} data-testid={`${testid}-count`}>
                {rows.length}
              </span>
            </div>
            <div className="text-xs text-md3-on-surface-variant">{subtitle}</div>
          </div>
        </div>
        <ChevronRight className={`h-4 w-4 text-md3-on-surface-variant transition-transform ${collapsed ? "" : "rotate-90"}`} />
      </button>
      {!collapsed && (
        <div className="border-t border-black/5">
          {rows.length === 0 ? (
            <div className="flex items-center gap-2 px-4 py-6 text-sm text-md3-on-surface-variant">
              <Check className="h-4 w-4 text-emerald-600" />
              {emptyText}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {!hideProject && <TableHead>Project</TableHead>}
                  {!hideProject && <TableHead>Client</TableHead>}
                  <TableHead>Submittal</TableHead>
                  <TableHead>Current Step</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Planned Date</TableHead>
                  {showOverdueDays && <TableHead className="text-right">Overdue</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow
                    key={r.submittal_id || i}
                    className="cursor-pointer hover:bg-black/[.03]"
                    onClick={() => rowClick?.(r.project_id)}
                    data-testid={`${testid}-row-${i}`}
                  >
                    {!hideProject && (
                    <TableCell className="font-mono text-xs">
                      {r.project_number ? (
                        <>
                          <span className="text-md3-primary font-semibold">{r.project_number}</span>
                          <span className="text-md3-on-surface-variant"> · {r.project_name || "—"}</span>
                        </>
                      ) : (
                        <span>{r.project_name || "—"}</span>
                      )}
                    </TableCell>
                    )}
                    {!hideProject && <TableCell>{r.client_name || "—"}</TableCell>}
                    <TableCell className="font-medium">{r.submittal_name || "—"}</TableCell>
                    <TableCell>{r.current_step_name || "—"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{r.current_status || "—"}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {r.planned_date ? String(r.planned_date).slice(0, 10) : "—"}
                    </TableCell>
                    {showOverdueDays && (
                      <TableCell className="text-right">
                        <Badge className="bg-rose-600 text-white text-xs">
                          {r.days_overdue}d
                        </Badge>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}
    </div>
  );
}
